import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { trackPageView } from "@/lib/analytics";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";

// Floating widgets — not needed for first paint, so they're lazy-loaded out
// of the main bundle instead of being parsed/executed on every page load.
const AgentMira = lazy(() => import("./components/AgentMira"));
const NewsletterPopup = lazy(() => import("./components/NewsletterPopup"));
const WhatsAppCTA = lazy(() => import("./components/WhatsAppCTA"));

// Pages — lazy loaded for code splitting
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const Contact = lazy(() => import("./pages/Contact"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const MobileAppDevelopment = lazy(() => import("./pages/MobileAppDevelopment"));
const AgenticAI = lazy(() => import("./pages/AgenticAI"));
const Legal = lazy(() => import("./pages/Legal"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));
// Agentic AI sub-pages (original 6)
const AIStrategy = lazy(() => import("./pages/agentic-ai/AIStrategy"));
const AIAgentsDev = lazy(() => import("./pages/agentic-ai/AIAgentsDev"));
const AILabs = lazy(() => import("./pages/agentic-ai/AILabs"));
const ProcessAutomation = lazy(() => import("./pages/agentic-ai/ProcessAutomation"));
const AIAnalytics = lazy(() => import("./pages/agentic-ai/AIAnalytics"));
const ManagedAI = lazy(() => import("./pages/agentic-ai/ManagedAI"));
// NEW Agentic AI sub-pages (from strategy doc)
const AIWorkflowOrchestration = lazy(() => import("./pages/agentic-ai/AIWorkflow"));
const VoiceAIAgents = lazy(() => import("./pages/agentic-ai/VoiceAI"));
const WhatsAppAI = lazy(() => import("./pages/agentic-ai/WhatsAppAI"));
const SmartTicketing = lazy(() => import("./pages/agentic-ai/SmartTicketing"));
const SentimentNLP = lazy(() => import("./pages/agentic-ai/SentimentNLP"));
const ManagedAIOps = lazy(() => import("./pages/agentic-ai/ManagedAIOps"));
const AikoshAdvantage = lazy(() => import("./pages/agentic-ai/AikoshAdvantage"));
const AITestingAgents = lazy(() => import("./pages/agentic-ai/AITestingAgents"));
const NanoGCC = lazy(() => import("./pages/NanoGCC"));
// Services sub-pages
const MobileAppDev = lazy(() => import("./pages/services/MobileAppDev"));
const GenerativeAI = lazy(() => import("./pages/services/GenerativeAI"));
const RPAAutomation = lazy(() => import("./pages/services/RPAAutomation"));
const CloudDevOps1 = lazy(() => import("./pages/services/CloudDevOps1"));
const WebApplicationDevelopment = lazy(() => import("./pages/services/WebApplicationDevelopment"));
const AnalyticsBI = lazy(() => import("./pages/services/AnalyticsBI"));
const IoTSolutions = lazy(() => import("./pages/services/IoTSolutions"));
const CustomWebApps = lazy(() => import("./pages/services/CustomWebApps"));
const Blockchain = lazy(() => import("./pages/services/Blockchain"));
// GEO city landing pages
const GeoGurugram = lazy(() => import("./pages/locations/Gurugram"));
const GeoMumbai = lazy(() => import("./pages/locations/Mumbai"));
const GeoBengaluru = lazy(() => import("./pages/locations/Bengaluru"));
const GeoHyderabad = lazy(() => import("./pages/locations/Hyderabad"));
const GeoPune = lazy(() => import("./pages/locations/Pune"));
const GeoChennai = lazy(() => import("./pages/locations/Chennai"));
// Blog articles
const AgenticAI90Days = lazy(() => import("./pages/blog/AgenticAI90Days"));
const DataSovereignty = lazy(() => import("./pages/blog/DataSovereignty"));
const VoiceAIRegionalLanguages = lazy(() => import("./pages/blog/VoiceAIRegionalLanguages"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
// Industry pages
const IndustryManufacturing = lazy(() => import("./pages/industries/Manufacturing"));
const IndustryBFSI = lazy(() => import("./pages/industries/BFSI"));
const IndustryHealthcare = lazy(() => import("./pages/industries/Healthcare"));
const IndustryRetail = lazy(() => import("./pages/industries/Retail"));
const IndustryTelecom = lazy(() => import("./pages/industries/Telecom"));
const IndustryGovernment = lazy(() => import("./pages/industries/Government"));
const IndustryEducation = lazy(() => import("./pages/industries/Education"));
const IndustryLogistics = lazy(() => import("./pages/industries/Logistics"));
// New full blog articles
const HondaInspection = lazy(() => import("./pages/blog/HondaInspection"));
const EVBrandFootfall = lazy(() => import("./pages/blog/EVBrandFootfall"));
const IndianSovereignAI = lazy(() => import("./pages/blog/IndianSovereignAI"));
const LLMComparison = lazy(() => import("./pages/blog/LLMComparison"));
const WhatsAppWorkflows = lazy(() => import("./pages/blog/WhatsAppWorkflows"));
const MonaWork = lazy(() => import("./pages/blog/MonaWork"));
const PartnerClaim = lazy(() => import("./pages/blog/PartnerClaim"));
const BharatGenKrutrim = lazy(() => import("./pages/blog/BharatGenKrutrim"));
// New service sub-pages (QA B-01)
const AIConsulting = lazy(() => import("./pages/services/AIConsulting"));
const AppDevelopment = lazy(() => import("./pages/services/AppDevelopment"));
const DigitalSecurity = lazy(() => import("./pages/services/DigitalSecurity"));
const DigitalMarketing = lazy(() => import("./pages/services/DigitalMarketing"));
const SapMachineLearning = lazy(() => import("./pages/services/SapMachineLearning"));
const AgenticUX = lazy(() => import("./pages/services/AgenticUX"));
// GEO & AI Search service pages
const GenerativeEngineOptimization = lazy(() => import("./pages/services/GenerativeEngineOptimization"));
const AnswerEngineOptimization = lazy(() => import("./pages/services/AnswerEngineOptimization"));
const ChatGptSeo = lazy(() => import("./pages/services/ChatGptSeo"));
const LlmOptimization = lazy(() => import("./pages/services/LlmOptimization"));
const AiOverviewOptimization = lazy(() => import("./pages/services/AiOverviewOptimization"));
const GeoServicesIndia = lazy(() => import("./pages/services/GeoServicesIndia"));
const AiSearchMarketing = lazy(() => import("./pages/services/AiSearchMarketing"));
const PerplexitySeo = lazy(() => import("./pages/services/PerplexitySeo"));
const AiCitationOptimization = lazy(() => import("./pages/services/AiCitationOptimization"));
const EntitySeo = lazy(() => import("./pages/services/EntitySeo"));
// Work pages (QA B-03)
const CaseStudiesWork = lazy(() => import("./pages/work/CaseStudiesWork"));
const ClientStories = lazy(() => import("./pages/work/ClientStories"));
// About sub-pages (QA B-02)
const OurStory = lazy(() => import("./pages/about/OurStory"));
const LifeAtSwaranSoft = lazy(() => import("./pages/about/LifeAtSwaranSoft"));
const Careers = lazy(() => import("./pages/Careers"));
// Phase 13: New blog articles from proposals
const AIComplaintManagement = lazy(() => import("./pages/blog/AIComplaintManagement"));
const AIVoiceAgentRetail = lazy(() => import("./pages/blog/AIVoiceAgentRetail"));
const ManufacturingAIQuote = lazy(() => import("./pages/blog/ManufacturingAIQuote"));
const EdgeAIHospitality = lazy(() => import("./pages/blog/EdgeAIHospitality"));
const GCCAIPlatform = lazy(() => import("./pages/blog/GCCAIPlatform"));
const WhatsAppAIEnterprise = lazy(() => import("./pages/blog/WhatsAppAIEnterprise"));
const RegionalLanguageApp = lazy(() => import("./pages/blog/RegionalLanguageApp"));
const AgenticAIStrategyBlog = lazy(() => import("./pages/blog/AgenticAIStrategy"));
// Phase 16: New blog articles (blog fix batch)
const AgenticAIvsRPA = lazy(() => import("./pages/blog/AgenticAIvsRPA"));
const BuildAILabsEnterprise = lazy(() => import("./pages/blog/BuildAILabsEnterprise"));
const AIBFSICompliance = lazy(() => import("./pages/blog/AIBFSICompliance"));
const AIPoliticalIntelligence = lazy(() => import("./pages/blog/AIPoliticalIntelligence"));
const PredictiveMaintenance = lazy(() => import("./pages/blog/PredictiveMaintenance"));
const AIHRAutomation = lazy(() => import("./pages/blog/AIHRAutomation"));
const GEOOptimisation = lazy(() => import("./pages/blog/GEOOptimisation"));
const N8NSupabaseStack = lazy(() => import("./pages/blog/N8NSupabaseStack"));
const AITelecomFieldOps = lazy(() => import("./pages/blog/AITelecomFieldOps"));
const AIHealthcareIndia = lazy(() => import("./pages/blog/AIHealthcareIndia"));
const AIReadinessAssessment = lazy(() => import("./pages/blog/AIReadinessAssessment"));
const SwaranSoft25Years = lazy(() => import("./pages/blog/SwaranSoft25Years"));
const AikoshEnterpriseGuide = lazy(() => import("./pages/blog/AikoshEnterpriseGuide"));
const SapMlCostReduction = lazy(() => import("./pages/blog/SapMlCostReduction"));
const HireFlowAI = lazy(() => import("./pages/blog/HireFlowAI"));
const AIPolicingIndia = lazy(() => import("./pages/blog/AIPolicingIndia"));
const WhatsAppB2BCommerce = lazy(() => import("./pages/blog/WhatsAppB2BCommerce"));
const VoiceAIBotsIndia = lazy(() => import("./pages/blog/VoiceAIBotsIndia"));
const TopAgenticAIPartners = lazy(() => import("./pages/blog/TopAgenticAIPartners"));
const AIChatbotVsRuleBased = lazy(() => import("./pages/blog/AIChatbotVsRuleBased"));
const DPDPCompliance = lazy(() => import("./pages/blog/DPDPCompliance"));
const DNSSEC = lazy(() => import("./pages/blog/DNSSEC"));
const BuildInHouseOrPartnerAI = lazy(() => import("./pages/blog/BuildInHouseOrPartnerAI"));
const CanAIRunBusinessOperations = lazy(() => import("./pages/blog/CanAIRunBusinessOperations"));
const AIConsultingVsITPartner = lazy(() => import("./pages/blog/AIConsultingVsITPartner"));
const ChoosingEnterpriseAICompany = lazy(() => import("./pages/blog/ChoosingEnterpriseAICompany"));
const PlanningAIJourney = lazy(() => import("./pages/blog/PlanningAIJourney"));
const WhatYouGainFromAgenticAI = lazy(() => import("./pages/blog/WhatYouGainFromAgenticAI"));
const BusinessAppIdeaScale = lazy(() => import("./pages/blog/BusinessAppIdeaScale"));
const BeforeYouHireAppDeveloper = lazy(() => import("./pages/blog/BeforeYouHireAppDeveloper"));
const BlockchainDataSecurity = lazy(() => import("./pages/blog/BlockchainDataSecurity"));
const BlockchainBuildVsHire = lazy(() => import("./pages/blog/BlockchainBuildVsHire"));
const DisconnectedDevicesIoT = lazy(() => import("./pages/blog/DisconnectedDevicesIoT"));
const IsIoTRightForYourBusiness = lazy(() => import("./pages/blog/IsIoTRightForYourBusiness"));
const DrowningInDataBI = lazy(() => import("./pages/blog/DrowningInDataBI"));
const StillRelyingOnManualReports = lazy(() => import("./pages/blog/StillRelyingOnManualReports"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const NewsletterVerify = lazy(() => import("./pages/NewsletterVerify"));
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const DiscoveryCallRedirect = lazy(() => import("./pages/DiscoveryCallRedirect"));

function RouteTracker() {
  const [location] = useLocation();
  useEffect(() => {
    trackPageView(location, document.title);
  }, [location]);
  return null;
}

function Router() {
  return (
    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
      <Switch>
        <Route path={"/"} component={Home} />
        <Route path={"/about"} component={About} />
        <Route path={"/services"} component={Services} />
        <Route path={"/case-studies"} component={CaseStudies} />
        <Route path={"/contact"} component={Contact} />
        <Route path={"/about/contact"} component={Contact} />
        <Route path={"/thank-you"} component={ThankYou} />
        {/* Blog */}
        <Route path={"/blog"} component={Blog} />
        <Route path={"/work/portfolio"} component={Portfolio} />
        <Route path={"/blog/agentic-ai-indian-enterprises-90-days"} component={AgenticAI90Days} />
        <Route path={"/blog/data-sovereignty-age-of-ai"} component={DataSovereignty} />
        <Route path={"/blog/voice-ai-regional-languages-enterprise"} component={VoiceAIRegionalLanguages} />
        {/* Legacy mobile app route — keep for backward compat */}
        <Route path={"/mobile-app-development"} component={MobileAppDevelopment} />
        {/* Agentic AI hub */}
        <Route path={"/agentic-ai"} component={AgenticAI} />
        {/* Agentic AI sub-pages (original 6) */}
        <Route path={"/agentic-ai/strategy"} component={AIStrategy} />
        <Route path={"/agentic-ai/development"} component={AIAgentsDev} />
        <Route path={"/agentic-ai/ai-labs"} component={AILabs} />
        <Route path={"/agentic-ai/automation"} component={ProcessAutomation} />
        <Route path={"/agentic-ai/analytics"} component={AIAnalytics} />
        <Route path={"/agentic-ai/managed"} component={ManagedAI} />
        {/* Agentic AI sub-pages (new 6 from strategy doc) */}
        <Route path={"/agentic-ai/workflow-orchestration"} component={AIWorkflowOrchestration} />
        <Route path={"/agentic-ai/voice-ai-agents"} component={VoiceAIAgents} />
        <Route path={"/agentic-ai/whatsapp-ai"} component={WhatsAppAI} />
        <Route path={"/agentic-ai/smart-ticketing"} component={SmartTicketing} />
        <Route path={"/agentic-ai/sentiment-nlp"} component={SentimentNLP} />
        <Route path={"/agentic-ai/managed-aiops"} component={ManagedAIOps} />
        <Route path={"/agentic-ai/aikosh-advantage"} component={AikoshAdvantage} />
        <Route path={"/agentic-ai/ai-testing-agents"} component={AITestingAgents} />
        <Route path={"/nano-gcc"} component={NanoGCC} />
        {/* Services sub-pages */}
        <Route path={"/services/mobile-app-development"} component={MobileAppDev} />
        <Route path={"/services/generative-ai"} component={GenerativeAI} />
        <Route path={"/services/rpa-automation"} component={RPAAutomation} />
        <Route path={"/services/cloud-devops"} component={CloudDevOps1} />
        <Route path={"/services/web-application-development"} component={WebApplicationDevelopment} />
        <Route path={"/services/analytics-bi"} component={AnalyticsBI} />
        <Route path={"/services/iot-solutions"} component={IoTSolutions} />
        <Route path={"/services/custom-web-apps"} component={CustomWebApps} />
        <Route path={"/services/blockchain"} component={Blockchain} />
        {/* New service sub-pages (QA B-01) */}
        <Route path={"/services/ai-consulting"} component={AIConsulting} />
        <Route path={"/services/app-development"} component={AppDevelopment} />
        <Route path={"/services/digital-security"} component={DigitalSecurity} />
        <Route path={"/services/ai-search-growth"} component={DigitalMarketing} />
        <Route path={"/services/sap-machine-learning"} component={SapMachineLearning} />
        <Route path={"/services/agentic-ux"} component={AgenticUX} />
        {/* GEO & AI Search service pages */}
        <Route path={"/services/generative-engine-optimization"} component={GenerativeEngineOptimization} />
        <Route path={"/services/answer-engine-optimization"} component={AnswerEngineOptimization} />
        <Route path={"/services/chatgpt-seo"} component={ChatGptSeo} />
        <Route path={"/services/llm-optimization"} component={LlmOptimization} />
        <Route path={"/services/ai-overviews-optimization"} component={AiOverviewOptimization} />
        <Route path={"/services/geo-services-india"} component={GeoServicesIndia} />
        <Route path={"/services/ai-search-marketing"} component={AiSearchMarketing} />
        <Route path={"/services/perplexity-seo"} component={PerplexitySeo} />
        <Route path={"/services/ai-citation-optimization"} component={AiCitationOptimization} />
        <Route path={"/services/entity-seo"} component={EntitySeo} />
        {/* Work pages (QA B-03) */}
        <Route path={"/work/case-studies"} component={CaseStudiesWork} />
        <Route path={"/work/client-stories"} component={ClientStories} />
        {/* About sub-pages (QA B-02) */}
        <Route path={"/about/our-story"} component={OurStory} />
        <Route path={"/about/life-at-swaran-soft"} component={LifeAtSwaranSoft} />
        <Route path={"/careers"} component={Careers} />
        {/* Industry pages */}
        <Route path={"/industries/manufacturing"} component={IndustryManufacturing} />
        <Route path={"/industries/bfsi"} component={IndustryBFSI} />
        <Route path={"/industries/healthcare"} component={IndustryHealthcare} />
        <Route path={"/industries/retail"} component={IndustryRetail} />
        <Route path={"/industries/telecom"} component={IndustryTelecom} />
        <Route path={"/industries/government"} component={IndustryGovernment} />
        <Route path={"/industries/education"} component={IndustryEducation} />
        <Route path={"/industries/logistics"} component={IndustryLogistics} />
        {/* Full blog articles */}
        <Route path={"/blog/honda-inspection"} component={HondaInspection} />
        <Route path={"/blog/ev-brand-footfall"} component={EVBrandFootfall} />
        <Route path={"/blog/indian-sovereign-ai-vs-openai"} component={IndianSovereignAI} />
        <Route path={"/blog/llm-comparison"} component={LLMComparison} />
        <Route path={"/blog/whatsapp-enterprise-workflows"} component={WhatsAppWorkflows} />
        <Route path={"/blog/monawork"} component={MonaWork} />
        <Route path={"/blog/partner-claim-spreadsheet"} component={PartnerClaim} />
        <Route path={"/blog/bharatgen-krutrim-guide"} component={BharatGenKrutrim} />
        {/* Phase 13: New blog articles from proposals */}
        <Route path={"/blog/ai-complaint-management-india"} component={AIComplaintManagement} />
        <Route path={"/blog/ai-voice-agent-retail-india"} component={AIVoiceAgentRetail} />
        <Route path={"/blog/manufacturing-ai-quote-automation"} component={ManufacturingAIQuote} />
        <Route path={"/blog/edge-ai-luxury-hospitality"} component={EdgeAIHospitality} />
        <Route path={"/blog/gcc-ai-platform-india"} component={GCCAIPlatform} />
        <Route path={"/blog/whatsapp-ai-enterprise-india"} component={WhatsAppAIEnterprise} />
        <Route path={"/blog/regional-language-enterprise-app"} component={RegionalLanguageApp} />
        <Route path={"/blog/agentic-ai-strategy-engagement"} component={AgenticAIStrategyBlog} />
        {/* Phase 16: New blog articles (blog fix batch) */}
        <Route path={"/blog/agentic-ai-vs-traditional-rpa"} component={AgenticAIvsRPA} />
        <Route path={"/blog/build-ai-labs-enterprise-india"} component={BuildAILabsEnterprise} />
        <Route path={"/blog/ai-bfsi-rbi-compliance-india"} component={AIBFSICompliance} />
        <Route path={"/blog/predictive-maintenance-manufacturing-india"} component={PredictiveMaintenance} />
        <Route path={"/blog/ai-hr-automation-india"} component={AIHRAutomation} />
        <Route path={"/blog/geo-optimisation-ai-search-engines"} component={GEOOptimisation} />
        <Route path={"/blog/n8n-supabase-enterprise-ai-stack"} component={N8NSupabaseStack} />
        <Route path={"/blog/ai-telecom-india-field-operations"} component={AITelecomFieldOps} />
        <Route path={"/blog/ai-healthcare-india-no-show-reduction"} component={AIHealthcareIndia} />
        <Route path={"/blog/ai-readiness-assessment-enterprise"} component={AIReadinessAssessment} />
        <Route path={"/blog/swaran-soft-25-years-enterprise-ai"} component={SwaranSoft25Years} />
        <Route path={"/blog/aikosh-enterprise-guide"} component={AikoshEnterpriseGuide} />
        <Route path={"/blog/sap-ml-cost-reduction"} component={SapMlCostReduction} />
        <Route path={"/blog/ai-political-intelligence-platform-india"} component={AIPoliticalIntelligence} />
        <Route path={"/blog/hireflow-ai-recruitment-platform"} component={HireFlowAI} />
        <Route path={"/blog/ai-first-policing-india-commissionerates"} component={AIPolicingIndia} />
        <Route path={"/blog/whatsapp-b2b-commerce-bot-order-automation"} component={WhatsAppB2BCommerce} />
        <Route path={"/blog/voice-ai-bots-india-call-centre-replacement"} component={VoiceAIBotsIndia} />
        <Route path={"/blog/top-agentic-ai-implementation-partners-india"} component={TopAgenticAIPartners} />
        <Route path={"/blog/ai-chatbot-vs-rule-based-bot"} component={AIChatbotVsRuleBased} />
        <Route path={"/blog/dpdp-act-2023-compliance-guide-indian-enterprises"} component={DPDPCompliance} />
        <Route path={"/blog/dnssec-protecting-your-domain"} component={DNSSEC} />
        <Route path={"/blog/build-ai-in-house-vs-partner"} component={BuildInHouseOrPartnerAI} />
        <Route path={"/blog/ai-agents-business-operations"} component={CanAIRunBusinessOperations} />
        <Route path={"/blog/ai-consulting-vs-it-partner"} component={AIConsultingVsITPartner} />
        <Route path={"/blog/choosing-enterprise-ai-company"} component={ChoosingEnterpriseAICompany} />
        <Route path={"/blog/ai-consulting-partner"} component={PlanningAIJourney} />
        <Route path={"/blog/agentic-ai-services"} component={WhatYouGainFromAgenticAI} />
        <Route path={"/blog/business-app-scaling"} component={BusinessAppIdeaScale} />
        <Route path={"/blog/app-developer-checklist"} component={BeforeYouHireAppDeveloper} />
        <Route path={"/blog/blockchain-data-security"} component={BlockchainDataSecurity} />
        <Route path={"/blog/blockchain-development-experts"} component={BlockchainBuildVsHire} />
        <Route path={"/blog/iot-operations"} component={DisconnectedDevicesIoT} />
        <Route path={"/blog/iot-for-business"} component={IsIoTRightForYourBusiness} />
        <Route path={"/blog/bi-data-decisions"} component={DrowningInDataBI} />
        <Route path={"/blog/business-intelligence-reporting"} component={StillRelyingOnManualReports} />
        {/* BlogPost wildcard — MUST be last among all /blog/* routes */}
        <Route path={"/blog/:slug"} component={BlogPost} />
        {/* GEO city landing pages — primary routes */}
        <Route path={"/agentic-ai-gurugram"} component={GeoGurugram} />
        <Route path={"/agentic-ai-mumbai"} component={GeoMumbai} />
        <Route path={"/agentic-ai-bengaluru"} component={GeoBengaluru} />
        <Route path={"/agentic-ai-hyderabad"} component={GeoHyderabad} />
        <Route path={"/agentic-ai-pune"} component={GeoPune} />
        <Route path={"/agentic-ai-chennai"} component={GeoChennai} />
        {/* GEO city landing pages — /locations/* alias routes (used by Navigation & Footer) */}
        <Route path={"/locations/gurugram"} component={GeoGurugram} />
        <Route path={"/locations/mumbai"} component={GeoMumbai} />
        <Route path={"/locations/bengaluru"} component={GeoBengaluru} />
        <Route path={"/locations/hyderabad"} component={GeoHyderabad} />
        <Route path={"/locations/pune"} component={GeoPune} />
        <Route path={"/locations/chennai"} component={GeoChennai} />
        {/* Legal pages — primary routes */}
        <Route path={"/privacy"} component={Legal} />
        <Route path={"/terms"} component={Legal} />
        <Route path={"/disclaimer"} component={Legal} />
        {/* Newsletter */}
        <Route path={"/newsletter/verify"} component={NewsletterVerify} />
        <Route path={"/newsletter/unsubscribe"} component={NewsletterUnsubscribe} />
        {/* Calendly redirect — fixes old emails that linked to this path */}
        <Route path={"/copilots-in/discovery-call"} component={DiscoveryCallRedirect} />
        {/* Sitemap */}
        <Route path={"/sitemap"} component={Sitemap} />
        {/* Legal pages — /legal/* alias routes (used by sitemap.xml) */}
        <Route path={"/legal/privacy"} component={Legal} />
        <Route path={"/legal/terms"} component={Legal} />
        <Route path={"/legal/disclaimer"} component={Legal} />
        <Route path={"/404"} component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <RouteTracker />
          <Router />
          <Suspense fallback={null}>
            <AgentMira />
            <NewsletterPopup />
            {/* WhatsApp stack above Agent Mira. */}
            <div className="fixed right-0 bottom-[90px] z-[70] flex flex-col-reverse gap-2.5 pointer-events-none">
              <div className="pointer-events-auto"><WhatsAppCTA /></div>
            </div>
          </Suspense>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
